import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate, useParams } from 'react-router-dom'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import SoapTemplateSection from '@/apps/legacy/components/portal/SoapTemplateSection'
import {
  useDrugSoapTemplatesQuery,
  useUpdateDrugSoapTemplatesMutation,
  useSoapTemplateEditor,
} from '@/features/internal/drugs'
import { useDrugDetailQuery } from '@/features/internal/drugs'
import { DrugGroup } from '@/features/internal/drugs/types/soapTemplate'
import { useToast } from '@/shared/contexts/ToastContext'
import {
  EditButton,
  BackButton,
  ActionButton,
  CancelButton,
} from '@/apps/legacy/components/portal/CustomButton'

/**
 * 医薬品詳細画面
 */
const DrugDetailPage: React.FC = () => {
  // URLから医薬品IDを取得
  const { id: drugId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast() // トースト通知用フック

  // 医薬品詳細取得（TanStack Query）
  const {
    data: drug,
    isLoading: drugLoading,
    error: drugError,
  } = useDrugDetailQuery(drugId)

  // 状態管理
  const [editMode, setEditMode] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [emptyItemsWarning, setEmptyItemsWarning] = useState<{
    show: boolean
    items: { groupName: string; soapType: string }[]
  }>({
    show: false,
    items: [],
  })

  // テンプレート関連のフック（責務分離）
  // データ取得（TanStack Query）
  const {
    templateGroups,
    isLoading: templatesLoading,
    error: templatesError,
  } = useDrugSoapTemplatesQuery(drugId)

  // ローカル編集状態管理
  const {
    editingTemplateGroups,
    resetChanges,
    addTemplateGroup,
    removeTemplateGroup,
    updateTemplateGroupName,
    updateTemplateGroupKeywords,
    addTemplateItem,
    removeTemplateItem,
    updateTemplateItem,
  } = useSoapTemplateEditor({ initialData: templateGroups })

  // 更新 Mutation
  const updateMutation = useUpdateDrugSoapTemplatesMutation()

  // 編集モード切替
  // 編集モードでは editingTemplateGroups を直接使用（useSoapTemplateEditor が状態管理）
  const toggleEditMode = () => {
    const newEditMode = !editMode
    setEditMode(newEditMode)
    // 編集モードを終了する場合は警告をクリア
    if (!newEditMode) {
      setEmptyItemsWarning({
        show: false,
        items: [],
      })
    }
  }

  // SOAPテンプレートグループを追加するハンドラ
  const handleAddTemplateGroup = () => {
    addTemplateGroup()
  }

  // 空のSOAP要素をチェックする関数
  const validateEmptyItems = () => {
    const emptyItems: { groupName: string; soapType: string }[] = []

    // 全てのテンプレートグループをチェック
    editingTemplateGroups.forEach((group) => {
      // 各グループ内のSOAP項目をチェック
      group.templates.forEach((item) => {
        // contentが空の場合は問題ありとマーク
        if (!item.content.trim()) {
          emptyItems.push({
            groupName: group.name,
            soapType: item.soap_type,
          })
        }
      })
    })

    return emptyItems
  }

  // 保存処理
  const handleSave = async () => {
    // 空のSOAP要素をチェック
    const emptyItems = validateEmptyItems()

    // 空の要素がある場合は警告を表示して処理中断
    if (emptyItems.length > 0) {
      setEmptyItemsWarning({
        show: true,
        items: emptyItems,
      })
      // 成功メッセージが表示されないようにリセット
      setSaveSuccess(false)
      return
    }

    // 警告表示をクリア
    setEmptyItemsWarning({
      show: false,
      items: [],
    })

    try {
      // SOAPテンプレート更新（Mutation使用）
      await updateMutation.mutateAsync({
        drugId,
        templateGroups: editingTemplateGroups,
      })

      // 成功時の処理
      setSaveSuccess(true)
      setEditMode(false)

      // 成功メッセージを表示した後に非表示
      setTimeout(() => {
        setSaveSuccess(false)
      }, 3000)
    } catch (_err) {
      // エラーの場合は成功メッセージを表示しない（Mutationがトースト表示）
      setSaveSuccess(false)
    }
  }

  // キャンセル処理
  const handleCancel = () => {
    if (window.confirm('編集内容が破棄されますが、よろしいですか？')) {
      resetChanges() // 編集内容を元に戻す
      setEditMode(false)
      // 警告をクリア
      setEmptyItemsWarning({
        show: false,
        items: [],
      })
    }
  }

  // 一覧へ戻る
  const handleBackToList = () => {
    navigate('/portal/soaps?tab=drugs')
  }

  // ローディング状態
  const isLoading = drugLoading || templatesLoading

  // エラーがあるかどうか
  const hasError = !!drugError || !!templatesError

  // 医薬品が所属するグループ
  // APIレスポンスから取得した所属グループを使用
  const belongingGroups = drug?.drug_groups || []

  return (
    <PortalLayout>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* パンくずリスト */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            onClick={(e) => {
              e.preventDefault()
              navigate('/portal/soaps')
            }}
            style={{ cursor: 'pointer' }}
          >
            SOAPテンプレート管理
          </Link>
          <Typography color="text.primary">
            {drug?.product_name || '医薬品詳細'}
          </Typography>
        </Breadcrumbs>

        {/* ヘッダー部分 */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" component="h1">
            医薬品詳細
          </Typography>
          {!editMode && (
            <EditButton variant="outlined" onClick={toggleEditMode}>
              SOAPテンプレートを編集
            </EditButton>
          )}
        </Box>
        <Divider sx={{ mb: 3 }} />

        {/* ローディング表示 */}
        {isLoading && (
          <Box my={4}>
            <LoadingIndicator />
          </Box>
        )}

        {/* 成功メッセージ */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            SOAPテンプレートが正常に更新されました。
          </Alert>
        )}

        {/* エラー表示 */}
        {hasError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            操作中にエラーが発生しました。しばらくしてから再度お試しください。
          </Alert>
        )}

        {!isLoading && drug && (
          <>
            {/* 医薬品基本情報 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                基本情報
              </Typography>

              <Grid container spacing={2} sx={{ ml: 1 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    医薬品名
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {drug.product_name}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    YJコード
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {drug.yj_code}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    一般名
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {drug.general_name}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    製造会社
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {drug.manufacturer_name || drug.manufacturer}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    単位
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {drug.standard_unit || '-(単位なし)'}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    薬価
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {drug.price}円
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* 所属グループ一覧 */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                所属グループ
              </Typography>

              {belongingGroups.length > 0 ? (
                <Box sx={{ ml: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {belongingGroups.map((group: DrugGroup) => (
                    <Chip
                      key={group.id}
                      label={group.name}
                      variant="outlined"
                      color="primary"
                      clickable
                      onClick={() => {
                        // グループIDが存在する場合のみナビゲーション
                        if (group.id) {
                          navigate(`/portal/soaps/groups/${group.id}`)
                        } else {
                          // エラーが発生した場合はユーザーに通知
                          if (showToast) {
                            showToast(
                              'この医薬品グループの詳細は表示できません',
                              'error'
                            )
                          }
                        }
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ ml: 1, color: 'text.secondary' }}
                >
                  所属するグループがありません
                </Typography>
              )}
            </Box>

            {/* SOAPテンプレート編集セクション */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                SOAPテンプレート
              </Typography>
              {(editMode && editingTemplateGroups.length > 0) ||
              (!editMode && templateGroups.length > 0) ? (
                (editMode ? editingTemplateGroups : templateGroups).map(
                  (group) => (
                    <SoapTemplateSection
                      key={group.id}
                      group={group}
                      editMode={editMode}
                      onGroupNameChange={
                        editMode
                          ? (groupId, name) => {
                              // EditingTemplateGroups内の該当グループ名を更新
                              const groupIdx = editingTemplateGroups.findIndex(
                                (g) => g.id === groupId
                              )
                              if (groupIdx !== -1) {
                                updateTemplateGroupName(groupIdx, name)
                              }
                            }
                          : () => {}
                      }
                      onKeywordsChange={
                        editMode
                          ? (groupId, keywords) => {
                              // EditingTemplateGroups内の該当グループのキーワードを更新
                              const groupIdx = editingTemplateGroups.findIndex(
                                (g) => g.id === groupId
                              )
                              if (groupIdx !== -1) {
                                updateTemplateGroupKeywords(groupIdx, keywords)
                              }
                            }
                          : () => {}
                      }
                      onAddItem={
                        editMode
                          ? (groupId, soapType) => {
                              // EditingTemplateGroups内の該当グループに項目を追加
                              const groupIdx = editingTemplateGroups.findIndex(
                                (g) => g.id === groupId
                              )
                              if (groupIdx !== -1) {
                                addTemplateItem(groupIdx, soapType)
                              }
                            }
                          : () => {}
                      }
                      onDeleteItem={
                        editMode
                          ? (groupId, itemId) => {
                              // EditingTemplateGroups内の該当グループから項目を削除
                              const groupIdx = editingTemplateGroups.findIndex(
                                (g) => g.id === groupId
                              )
                              if (groupIdx !== -1) {
                                const itemIdx = editingTemplateGroups[
                                  groupIdx
                                ].templates.findIndex(
                                  (item) => item.id === itemId
                                )
                                if (itemIdx !== -1) {
                                  removeTemplateItem(groupIdx, itemIdx)
                                }
                              }
                            }
                          : () => {}
                      }
                      onItemContentChange={
                        editMode
                          ? (groupId, itemId, content) => {
                              // EditingTemplateGroups内の該当項目の内容を更新
                              const groupIdx = editingTemplateGroups.findIndex(
                                (g) => g.id === groupId
                              )
                              if (groupIdx !== -1) {
                                const itemIdx = editingTemplateGroups[
                                  groupIdx
                                ].templates.findIndex(
                                  (item) => item.id === itemId
                                )
                                if (itemIdx !== -1) {
                                  updateTemplateItem(groupIdx, itemIdx, content)
                                }
                              }
                            }
                          : () => {}
                      }
                      onDeleteGroup={
                        editMode
                          ? (groupId) => {
                              // EditingTemplateGroupsから該当グループを削除
                              const groupIdx = editingTemplateGroups.findIndex(
                                (g) => g.id === groupId
                              )
                              if (groupIdx !== -1) {
                                removeTemplateGroup(groupIdx)
                              }
                            }
                          : () => {}
                      }
                    />
                  )
                )
              ) : (
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    backgroundColor: colorTokens.background.subtle,
                  }}
                >
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    SOAPテンプレートが存在しません
                  </Typography>
                  {editMode && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddTemplateGroup}
                    >
                      新しいSOAPテンプレートグループを作成
                    </Button>
                  )}
                </Paper>
              )}
              {editMode && editingTemplateGroups.length > 0 && (
                <ActionButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddTemplateGroup}
                  sx={{ mt: 2 }}
                >
                  新しいSOAPテンプレートグループを追加
                </ActionButton>
              )}
            </Box>

            {/* 空SOAP要素警告メッセージ（ボタン直前に表示） */}
            {emptyItemsWarning.show && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold">
                  空のSOAP項目があります
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  以下のSOAP項目に内容が入力されていません。
                  項目に内容を入力するか、不要な項目は削除してください。
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {emptyItemsWarning.items.map((item, index) => (
                    <Box component="li" key={index}>
                      {item.groupName}：{item.soapType}項目
                    </Box>
                  ))}
                </Box>
              </Alert>
            )}

            {/* アクションボタン */}
            {editMode ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 4,
                }}
              >
                <CancelButton variant="outlined" onClick={handleCancel}>
                  キャンセル
                </CancelButton>
                <ActionButton
                  variant="contained"
                  onClick={handleSave}
                  disabled={isLoading}
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {isLoading ? '保存中...' : '保存'}
                </ActionButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 4,
                }}
              >
                <BackButton variant="outlined" onClick={handleBackToList}>
                  一覧へ戻る
                </BackButton>
              </Box>
            )}
          </>
        )}
      </Paper>
    </PortalLayout>
  )
}

export default DrugDetailPage
